import React from 'react'
import './access-policy-user-role-editor.scss'
import { Well } from '@pushly/aqe/lib/components'
import { Table, Input, Flex } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { usePrevious } from '@halo/hooks/use-previous'
import * as deepEqual from 'react-fast-compare'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { IUserRole } from '@halo/interfaces/user-role'

interface IAccessPolicyUserRoleEditorProps {
    className?: string
    title?: string
    value?: number[]
    defaultValue?: number[]
    onChange: (roleIds: number[], roles: IUserRole[]) => any
    options: IUserRole[]
    actions?: React.ReactNode
}

const AccessPolicyUserRoleEditor: React.FunctionComponent<IAccessPolicyUserRoleEditorProps> = (props) => {
    const prevOptions = usePrevious(props.options)
    const [visibleOptions, setVisibleOptions] = React.useState<IUserRole[]>(props.options)
    const [value, setValue] = React.useState<number[]>(props.value ?? props.defaultValue ?? [])
    const [searchValue, setSearchValue] = React.useState<string>('')

    const searchUserRoles = React.useCallback(
        async (search: string) => {
            setSearchValue(search)

            if (!search.trim()) {
                setVisibleOptions(props.options)
            } else {
                const searchRgx = new RegExp(`${search}`, 'i')
                setVisibleOptions(
                    props.options.filter(
                        (role) => searchRgx.test(role.name) || (role.description && searchRgx.test(role.description)),
                    ),
                )
            }
        },
        [JSON.stringify(props.options)],
    )

    React.useEffect(() => {
        const prevOptionIds = prevOptions.map((ap) => ap.id)
        prevOptionIds.sort()
        const currOptionIds = props.options.map((ap) => ap.id)
        currOptionIds.sort()

        if (!deepEqual(prevOptionIds, currOptionIds)) {
            searchUserRoles(searchValue)
        }
    }, [JSON.stringify(props.options)])

    const tableHeader = (
        <Flex gap={8} justify="space-between" align="center">
            <Flex flex={1} gap={8} align="center">
                <Input.Search
                    style={{ width: 300 }}
                    size="middle"
                    placeholder="Search ..."
                    onSearch={searchUserRoles}
                    onChange={(ev) => searchUserRoles(ev.target.value)}
                />
            </Flex>
        </Flex>
    )

    return (
        <Well
            className={classnames('access-policy-user-role-editor', 'type-2', props.className)}
            title={props.title ?? 'User Roles'}
            actions={props.actions}
            hideFooter={true}
        >
            <Table<IUserRole>
                title={() => tableHeader}
                size="small"
                bordered={false}
                dataSource={visibleOptions}
                rowKey={(row) => row.id}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: props.value ?? value,
                    hideSelectAll: true,
                    onChange: (_selectedKeys: React.Key[], selectedRows: IUserRole[]) => {
                        const nextSelectedIds = selectedRows.map((ap) => ap.id)
                        setValue(nextSelectedIds)
                        props.onChange(nextSelectedIds, selectedRows)
                    },
                }}
            >
                <Table.Column
                    className="user-role"
                    title="User Role"
                    dataIndex="name"
                    render={(_, role: IUserRole) => {
                        const hasDesc = !!role.description
                        const name = role.description ?? role.name

                        return (
                            <TableRowEntityDisplay
                                title={
                                    <span>
                                        {name}
                                        {hasDesc && <span className="role-name">&nbsp;({role.name})</span>}
                                    </span>
                                }
                            />
                        )
                    }}
                />
            </Table>
        </Well>
    )
}

export default AccessPolicyUserRoleEditor
